import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import styles from "./ConnectDrivePopup.module.css";

import {
  isNativeApp,
  requestNativeDriveConnect,
  DriveConnectResult,
} from "../../utils/nativeDrive";

/**
 * ConnectDrivePopup — shown to Law Firm agents on a paid plan
 * when Google Drive is not yet connected. Without Drive the
 * agent cannot store call recordings / transcripts, so we
 * surface the warning before they go live.
 *
 * @param {boolean} isOpen
 * @param {string}  userId
 * @param {string}  agentId
 * @param {() => void} onClose      Called when user dismisses ("Do it later").
 * @param {() => void} onConnected  Called after a successful connect (no auth flow needed).
 */
const ConnectDrivePopup = ({ isOpen, userId, agentId, onClose, onConnected }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // Drops the native listener if the popup unmounts mid-flow.
  const nativeCleanupRef = useRef(null);
  useEffect(() => () => nativeCleanupRef.current?.(), []);

  const handleConnect = async () => {
    if (!userId || !agentId) {
      setError("Missing user / agent info. Please refresh and try again.");
      return;
    }
    setError("");
    setLoading(true);

    // ── Native app: the shell owns the Google sheet ──────────────────
    // Google refuses its OAuth screen inside a WebView (403
    // disallowed_useragent), so the web side can never run the consent when
    // hosted by the RN app. Web (plain browser) keeps the original authUrl
    // redirect below, completely untouched.
    if (isNativeApp()) {
      const reply = await requestNativeDriveConnect(userId, agentId, (fn) => {
        nativeCleanupRef.current = fn;
      });
      nativeCleanupRef.current = null;
      setLoading(false);

      if (reply.type === DriveConnectResult.CONNECTED) {
        onConnected?.();
        onClose?.();
        return;
      }
      // Cancelled = user's own choice, no scary red bar.
      if (reply.type === DriveConnectResult.CANCELLED) return;

      setError(
        reply?.data?.message || "Drive connection failed. Please try again.",
      );
      return;
    }

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/connect-drive`,
        // Send the current domain so the OAuth callback returns the user here
        // (same as Calendar / Google login), not to the default App_Url domain.
        { userId, agentId, origin: window.location.origin }
      );

      if (res.data?.alreadyConnected) {
        setLoading(false);
        onConnected?.();
        onClose?.();
        return;
      }

      if (res.data?.authUrl) {
        // Google consent screen. After auth the user is redirected
        // back to /fast-agent-detail?drive=connected — FooterNew's
        // existing useEffect handles the post-redirect status refresh.
        window.location.href = res.data.authUrl;
        return;
      }

      setLoading(false);
      setError("Could not start Drive connection. Please try again.");
    } catch (err) {
      console.error("[ConnectDrivePopup] connect failed:", err);
      setLoading(false);
      setError("Drive connection failed. Please try again.");
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.overlay}>
      <div className={styles.popup} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <div className={styles.iconWrap}>
            <svg width="34" height="34" viewBox="0 0 87 78" xmlns="http://www.w3.org/2000/svg">
              <path d="M6.6 66.8L10.5 73.3C11.3 74.7 12.4 75.8 13.7 76.6L27.6 52.5H0C0 53.9 0.4 55.3 1.2 56.7L6.6 66.8Z" fill="#0066DA"/>
              <path d="M43.4 25.5L29.5 1.4C28.2 2.2 27.1 3.3 26.3 4.7L1.2 48.1C0.4 49.5 0 50.9 0 52.3H27.6L43.4 25.5Z" fill="#00AC47"/>
              <path d="M73.1 76.6C74.4 75.8 75.5 74.7 76.3 73.3L77.9 70.5L85.7 56.7C86.5 55.3 86.9 53.9 86.9 52.5H59.3L65.1 64.8L73.1 76.6Z" fill="#EA4335"/>
              <path d="M43.4 25.5L57.3 1.4C56 0.6 54.5 0.2 53.0 0.2H33.9C32.4 0.2 30.9 0.7 29.5 1.4L43.4 25.5Z" fill="#00832D"/>
              <path d="M59.3 52.5H27.6L13.7 76.6C15 77.4 16.5 77.8 18.0 77.8H68.9C70.4 77.8 71.9 77.4 73.1 76.6L59.3 52.5Z" fill="#2684FC"/>
              <path d="M73.0 27L60.5 5.3C59.7 3.9 58.6 2.8 57.3 2.0L43.4 26.1L59.3 53H86.8C86.8 51.6 86.4 50.2 85.6 48.8L73.0 27Z" fill="#FFBA00"/>
            </svg>
          </div>
          <h3 className={styles.title}>Connect Google Drive</h3>
          <p className={styles.subtitle}>
            Connect your Drive so every call recording and transcript is
            saved automatically to your account.
          </p>
        </div>

        <div className={styles.warningBox}>
          <span className={styles.warningIcon}>⚠️</span>
          <p className={styles.warningText}>
            <strong>Action required:</strong> Without Drive connected, your
            agent <strong>won't be able to attend calls</strong>. Connect now
            to keep your agent live.
          </p>
        </div>

        {error && <div className={styles.errorBar}>{error}</div>}

        <div className={styles.actions}>
          <button
            className={styles.connectBtn}
            onClick={handleConnect}
            disabled={loading}
          >
            {loading ? (
              <>
                <span className={styles.spinnerSmall} />
                Connecting...
              </>
            ) : (
              "Connect Drive"
            )}
          </button>
          <button
            className={styles.laterBtn}
            onClick={onClose}
            disabled={loading}
          >
            Do it later
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConnectDrivePopup;
